import React, { useCallback, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import Icon from 'components/Icon'
import { useClickOutSide, useDeleteItem } from 'hooks'
import { addModal } from 'features/modals/modalSlice'
import { deleteNavigationItem } from '../graphql/mutation'
import ContextMenu from './ContextMenu'
import OptionButton from './OptionButton'
import NavMenuEditor from './NavMenuEditor'
import { NavigationItem } from '.'

const S = {
    Wrapper: styled(ContextMenu.Wrapper)`
        cursor: pointer;
        display: flex;
        align-items: center;
        margin-right: 10px;
        background: #fff;
    `,
    Label: styled(ContextMenu.Trigger)`
        background: transparent;
        color: rgba(45, 48, 53, 0.75);
        white-space: nowrap;
        margin: -5px;
        padding: 5px;
    `,
}

type Props = {
    item: NavigationItem
    onDeleted: () => void
}

const NavItem = ({ item: initItem, onDeleted, ...dragHandleProps }: Props) => {
    const [item, setItem] = useState(initItem)
    const [subMenusShowed, showSubMenus] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>()
    const dispatch = useDispatch()
    const { showDeleteConfirmModal } = useDeleteItem(deleteNavigationItem, onDeleted)
    useClickOutSide(dropdownRef, () => subMenusShowed && showSubMenus(false))

    const showEditNavItemModal = useCallback((e) => {
        e.stopPropagation()
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <NavMenuEditor
                    close={close}
                    onSubmitted={
                        (updatedItem, isReplaced) => (isReplaced ? onDeleted() : setItem(updatedItem))
                    }
                    navMenu={{
                        id: item.id,
                        title: item.text,
                        order: item.order,
                        spaceId: item.space.id,
                        linkedSpaceId: item.linkedSpace?.id
                    }}
                />
            ),
            shouldNotWrap: true
        }))
    }, [dispatch, item.id, item.linkedSpace?.id, item.order, item.space.id, item.text, onDeleted])

    const handleDelete = useCallback(() => {
        showDeleteConfirmModal(item.id)
    }, [item.id, showDeleteConfirmModal])

    return (
        <S.Wrapper>
            <div {...dragHandleProps}>
                <Icon icon="dragVertical" style={{ marginBottom: 2 }} />
            </div>
            <S.Label>
                {item.text}

                <ContextMenu.Container>
                    <OptionButton icon="edit" onClick={showEditNavItemModal} />
                    <OptionButton icon="view" active />
                    <OptionButton icon="trash" onClick={handleDelete} />
                </ContextMenu.Container>
            </S.Label>
        </S.Wrapper>
    )
}

export default NavItem
